/**
 * CreateCampaignPanel
 *
 * Right-side slide-in form for creating a new campaign.
 *
 * Field order: Groups chip-multi-select → Title → Description →
 *              MediaPicker → date + time inputs.
 *
 * Terminal actions:
 *   - Schedule (primary)    — enabled: Title + Description non-empty + ≥1 group + assetCount > 0
 *   - Save as draft (secondary) — enabled: Title non-empty
 *
 * Live deduped asset count displayed under Groups (250ms debounced server
 * query, cleanup on unmount — see useCampaignFormState).
 *
 * Mutation flow:
 *   - Save-as-draft → useCreateCampaign with status=DRAFT
 *   - Schedule      → useCreateCampaign with status=SCHEDULED + scheduledSendAt
 *     (the Phase 2 dispatchScheduledCampaigns timer fires automatically; we
 *      do NOT call useDispatchCampaign here)
 *
 * Guards:
 *   - currentWorkspace?.id is checked before render — returns null early if
 *     undefined to avoid passing undefined to mutations (per CLAUDE.md).
 */
import {
  CampaignStatus,
  EnvConfig,
  MediaData,
  MediaType,
  PickerMode,
  useAllWorkspaceGroups,
  useCurrentWorkspace,
} from '@gripp/shared-logic';
import {
  Colors,
  Button,
  TextInput as CCTextInput,
  ConfirmationOverlay,
  DatePickerSelector,
  FormContext,
  GroupSelector,
  Icon,
  tokens,
  useSlideLayout,
} from '@gripp/shared-ui';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TimePicker from 'react-time-picker';
import 'react-time-picker/dist/TimePicker.css';
import {
  ActivityIndicator,
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MediaPicker } from '../../shared/components/media/mediaPicker';
import {
  useCreateCampaign,
  useFindMediaByIds,
  useGetCampaign,
  useUpdateCampaign,
  useDispatchCampaign,
} from '../hooks/useCampaigns';
import { useCampaignFormState } from '../hooks/useCampaignFormState';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Combine date string (YYYY-MM-DD) and time string (HH:MM) into an ISO string. */
const buildScheduledSendAt = (date: string, time: string): string => {
  const safeTime = time || '09:00';
  const safeDate = date || new Date().toISOString().slice(0, 10);
  return new Date(`${safeDate}T${safeTime}:00`).toISOString();
};

// One-time global style for the react-time-picker used in the schedule shell.
// We strip the library's default border + background so it inherits the shell
// styling (neutral[200] fill) and remove its built-in clock icon since we
// render our own Clock A next to it.
if (typeof document !== 'undefined') {
  const id = 'gripp-campaign-time-picker-style';
  if (!document.getElementById(id)) {
    const styleEl = document.createElement('style');
    styleEl.id = id;
    styleEl.textContent = `
      .gripp-campaign-time-picker { width: 100%; }
      .gripp-campaign-time-picker .react-time-picker__wrapper {
        border: none;
        background: transparent;
        padding: 0;
      }
      .gripp-campaign-time-picker .react-time-picker__inputGroup {
        font-family: 'Roboto', 'Helvetica Neue', Helvetica, Arial, sans-serif;
        font-size: 14px;
        color: #000;
      }
      .gripp-campaign-time-picker input { outline: none; }
      .gripp-campaign-time-picker .react-time-picker__clear-button,
      .gripp-campaign-time-picker .react-time-picker__clock-button {
        display: none;
      }
      .gripp-campaign-time-picker .react-time-picker__clock { display: none; }
    `;
    document.head.appendChild(styleEl);
  }
}

// --- Schedule date+time picker (uses the shared DatePickerSelector) ----------
// DatePickerSelector expects react-hook-form `control` + `setValue` and, for
// mode="time" on web, a surrounding FormProvider (WebDateTimePicker pulls
// `useFormContext`). Parent state still lives in useCampaignFormState; this
// component is the bridge.

const ymdToMMDDYYYY = (ymd: string): string => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-');
  if (!y || !m || !d) return '';
  return `${m}/${d}/${y}`;
};
const mmddyyyyToYMD = (mdy: string): string => {
  if (!mdy) return '';
  const [m, d, y] = mdy.split('/');
  if (!m || !d || !y) return '';
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
};
// scheduledTime (parent) is local "HH:MM". WebDateTimePicker stores ISO UTC.
const hhmmToISO = (hhmm: string): string => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toISOString();
};
const isoToLocalHHMM = (iso: string): string => {
  if (!iso || !iso.includes('T')) return iso || '';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes()
  ).padStart(2, '0')}`;
};

interface ScheduleDateTimePickerProps {
  date: string;
  time: string;
  onDateChange: (date: string) => void;
  onTimeChange: (time: string) => void;
}

const ScheduleDateTimePicker = ({
  date,
  time,
  onDateChange,
  onTimeChange,
}: ScheduleDateTimePickerProps) => {
  const form = useForm({
    defaultValues: {
      scheduledDate: ymdToMMDDYYYY(date),
      scheduledTime: hhmmToISO(time),
    },
  });
  const { control, setValue, watch } = form;

  // Sync incoming parent state into the form (e.g., when hydrating an edit).
  useEffect(() => {
    setValue('scheduledDate', ymdToMMDDYYYY(date), { shouldDirty: false });
  }, [date, setValue]);
  useEffect(() => {
    setValue('scheduledTime', hhmmToISO(time), { shouldDirty: false });
  }, [time, setValue]);

  // Propagate form changes back out to parent state.
  useEffect(() => {
    const sub = watch((values, { name }) => {
      if (name === 'scheduledDate') {
        const next = mmddyyyyToYMD(values.scheduledDate || '');
        if (next !== date) onDateChange(next);
      }
      if (name === 'scheduledTime') {
        const next = isoToLocalHHMM(values.scheduledTime || '');
        if (next !== time) onTimeChange(next);
      }
    });
    return () => sub.unsubscribe();
  }, [watch, date, time, onDateChange, onTimeChange]);

  const today = new Date();
  const todayYMD = `${today.getFullYear()}-${String(
    today.getMonth() + 1
  ).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // WebDateTimePicker reads from shared-ui's local FormContext (not RHF's
  // built-in FormProvider — comment in formContext.ts notes RHF's was
  // returning null in this codebase). The time picker doesn't ship with the
  // same `dateRangeFieldContainer` shell as the date picker, so we render the
  // time input ourselves inside a shell that matches.
  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <FormContext.Provider value={form as any}>
      <View style={styles.dateTimeRow}>
        <View style={styles.dateInputWrapper}>
          <DatePickerSelector
            name="scheduledDate"
            control={control}
            setValue={setValue}
            mode="date"
            placeholder="MM/DD/YYYY"
            maxYears={5}
            minDate={todayYMD}
          />
        </View>
        <View style={styles.timeInputWrapper}>
          <View style={styles.timeFieldShell}>
            <View style={styles.timePickerInnerWrapper}>
              <TimePicker
                onChange={(v) =>
                  onTimeChange(typeof v === 'string' ? v : '')
                }
                value={time || null}
                format="hh:mm a"
                disableClock
                clearIcon={null}
                clockIcon={null}
                className="gripp-campaign-time-picker"
              />
            </View>
            <Icon
              name="Clock A"
              size={18}
              color={Colors.role.textStrong}
            />
          </View>
        </View>
      </View>
    </FormContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// CreateCampaignPanelInner — rendered only when workspaceId is available
// ---------------------------------------------------------------------------

interface CreateCampaignPanelInnerProps {
  workspaceId: string;
  campaignId?: string;
  onClose?: () => void;
  onCreated?: (campaignId: string) => void;
  onUpdated?: (campaignId: string) => void;
  /** Opens the groups picker in a sibling slide-in column (campaignsPage). */
  onOpenGroupsPanel?: () => void;
}

const CreateCampaignPanelInner = ({
  workspaceId,
  campaignId,
  onClose,
  onCreated,
  onUpdated,
  onOpenGroupsPanel,
}: CreateCampaignPanelInnerProps) => {
  const { t } = useTranslation();
  const isEditMode = !!campaignId;
  const { mutateAsync: createCampaign, isPending: isCreating } =
    useCreateCampaign();
  const { mutateAsync: updateCampaign, isPending: isUpdating } =
    useUpdateCampaign();
  const { mutateAsync: dispatchCampaign, isPending: isDispatching } =
    useDispatchCampaign();
  const { data: campaignData } = useGetCampaign(campaignId ?? '');

  // Schedule / Send-now confirmations render as a column overlay (the shared
  // ConfirmationOverlay), matching the delete confirm in ActivityEditColumn —
  // rather than a bespoke centered Modal.
  const { setColumnOverlay } = useSlideLayout();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useCampaignFormState(workspaceId);

  const {
    title,
    description,
    selectedGroups,
    mediaIds,
    scheduledDate,
    scheduledTime,
    setTitle,
    setDescription,
    setSelectedGroups,
    addMediaId,
    removeMediaId,
    setScheduledDate,
    setScheduledTime,
    canSchedule,
    canSaveDraft,
    assetCount,
    isLoadingAssetCount,
    reset,
  } = form;

  // Groups use the standard MultiItemSelector (dark pills + circular '+') via
  // GroupSelector. Edit mode hydrates selectedGroups as { id, name: '' }
  // placeholders (getCampaign returns only groupIds) — resolve the names from
  // the workspace group list so the pills render with labels.
  const { groups: availableGroups } = useAllWorkspaceGroups(workspaceId);
  const resolvedSelectedGroups = useMemo(() => {
    const byId = new Map(availableGroups.map((g) => [g.id, g]));
    return selectedGroups.map((g) =>
      g.name ? g : byId.get(g.id) ?? g
    );
  }, [selectedGroups, availableGroups]);

  // ---- Media picker state ----
  const [mediaItems, setMediaItems] = useState<MediaData[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Resolve already-attached media records for edit mode. Campaign schema
  // stores only mediaIds — we fetch the full Media records so the thumbnail
  // grid can render them.
  const { data: existingMediaData } = useFindMediaByIds(mediaIds);
  const seededMediaIds = useRef(new Set<string>());
  useEffect(() => {
    const items = (
      existingMediaData as
        | { findMedia?: { items?: MediaData[] } }
        | undefined
    )?.findMedia?.items;
    if (!items?.length) return;
    setMediaItems((prev) => {
      const next = [...prev];
      let changed = false;
      items.forEach((m) => {
        if (!m.id || seededMediaIds.current.has(m.id)) return;
        if (!next.some((x) => x.id === m.id)) {
          next.push(m);
          changed = true;
        }
        seededMediaIds.current.add(m.id);
      });
      return changed ? next : prev;
    });
  }, [existingMediaData]);

  // ---- Hydrate form when editing an existing campaign ----
  const hydratedFromCampaignId = useRef<string | null>(null);
  useEffect(() => {
    if (!campaignId) return;
    if (hydratedFromCampaignId.current === campaignId) return;
    const campaign = (
      campaignData as
        | {
            campaign?: {
              getCampaign?: {
                id?: string;
                title?: string;
                description?: string;
                groupIds?: string[];
                mediaIds?: string[];
                scheduledSendAt?: string | null;
              };
            };
          }
        | undefined
    )?.campaign?.getCampaign;
    if (!campaign?.id) return;

    setTitle(campaign.title ?? '');
    setDescription(campaign.description ?? '');
    setSelectedGroups(
      (campaign.groupIds ?? []).map((id) => ({ id, name: '' }))
    );
    (campaign.mediaIds ?? []).forEach((id) => addMediaId(id));
    if (campaign.scheduledSendAt) {
      // scheduledSendAt is stored UTC. The form holds LOCAL date + time (the
      // write path goes back through `new Date(...).toISOString()` to convert
      // back to UTC). Use local getters to mirror that round-trip.
      const d = new Date(campaign.scheduledSendAt);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const mi = String(d.getMinutes()).padStart(2, '0');
      setScheduledDate(`${yyyy}-${mm}-${dd}`);
      setScheduledTime(`${hh}:${mi}`);
    }
    hydratedFromCampaignId.current = campaignId;
  }, [
    campaignId,
    campaignData,
    setTitle,
    setDescription,
    setSelectedGroups,
    addMediaId,
    setScheduledDate,
    setScheduledTime,
  ]);

  const handleSetMedia = (item: MediaData) => {
    if (!item.id) return;
    setMediaItems((prev) =>
      prev.some((m) => m.id === item.id) ? prev : [...prev, item]
    );
    addMediaId(item.id);
  };

  const handleRemoveMedia = (id: string) => {
    setMediaItems((prev) => prev.filter((m) => m.id !== id));
    removeMediaId(id);
  };

  const handleSetProgress = (_mediaId: string, progress: number) => {
    setUploadProgress(progress);
  };

  // ---- Save as draft ----
  const handleSaveDraft = async () => {
    if (!canSaveDraft || isCreating || isUpdating) return;

    try {
      setIsSubmitting(true);
      if (isEditMode && campaignId) {
        await updateCampaign({
          input: {
            id: campaignId,
            title: title.trim(),
            description: description.trim(),
            groupIds: selectedGroups.map((g) => g.id),
            mediaIds,
            status: CampaignStatus.Draft,
          },
        });
        onUpdated?.(campaignId);
      } else {
        const result = await createCampaign({
          input: {
            workspace: { id: workspaceId },
            title: title.trim(),
            description: description.trim(),
            groupIds: selectedGroups.map((g) => g.id),
            mediaIds,
            status: CampaignStatus.Draft,
            replyCount: 0,
          },
        });
        const created = (
          result as { id?: string } | null | undefined
        );
        if (created?.id) {
          onCreated?.(created.id);
        }
      }
      // Only clear the form in create mode. In edit mode the panel instance can
      // be reused on reopen; resetting here empties it while the hydration latch
      // (hydratedFromCampaignId) still matches, so the reopened panel would show
      // blank fields until a full reload. The panel closes either way.
      if (!isEditMode) reset();
      onClose?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---- Schedule ----
  const handleConfirmSchedule = async () => {
    if (!canSchedule || isCreating || isUpdating) return;

    try {
      setIsSubmitting(true);
      const scheduledSendAt = buildScheduledSendAt(scheduledDate, scheduledTime);
      if (isEditMode && campaignId) {
        await updateCampaign({
          input: {
            id: campaignId,
            title: title.trim(),
            description: description.trim(),
            groupIds: selectedGroups.map((g) => g.id),
            mediaIds,
            status: CampaignStatus.Scheduled,
            scheduledSendAt,
          },
        });
        onUpdated?.(campaignId);
      } else {
        const result = await createCampaign({
          input: {
            workspace: { id: workspaceId },
            title: title.trim(),
            description: description.trim(),
            groupIds: selectedGroups.map((g) => g.id),
            mediaIds,
            status: CampaignStatus.Scheduled,
            scheduledSendAt,
            replyCount: 0,
          },
        });
        const created = (
          result as { id?: string } | null | undefined
        );
        if (created?.id) {
          onCreated?.(created.id);
        }
      }
      setColumnOverlay(null);
      // Only clear the form in create mode. In edit mode the panel instance can
      // be reused on reopen; resetting here empties it while the hydration latch
      // (hydratedFromCampaignId) still matches, so the reopened panel would show
      // blank fields until a full reload. The panel closes either way.
      if (!isEditMode) reset();
      onClose?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSchedulePress = () => {
    if (!canSchedule) return;
    const displayDate = scheduledDate || new Date().toISOString().slice(0, 10);
    const displayTime = scheduledTime || '09:00';
    setColumnOverlay(
      <ConfirmationOverlay
        title={t('campaigns.create.confirm.title', 'Schedule campaign?')}
        subtitle={t(
          'campaigns.create.confirm.body',
          'Schedule campaign for {{count}} assets at {{date}} {{time}}?',
          { count: assetCount, date: displayDate, time: displayTime }
        )}
        confirmLabel={t('campaigns.create.confirm.confirm', 'Confirm schedule')}
        confirmState="active"
        onConfirm={handleConfirmSchedule}
        cancelLabel={t('common.cancel', 'Cancel')}
        onCancel={() => setColumnOverlay(null)}
        testID="schedule-confirm-overlay"
      />
    );
  };

  // ---- Send now ----
  // Same validation gate as Schedule (title + description + ≥1 group + assets).
  // Saves the current form first (create-as-DRAFT for new, or update for edit),
  // then immediately calls dispatchCampaign. Skips the timer; uses the same
  // dispatchCampaignInternal code path so authorship + side effects are
  // identical to a timer-driven send.
  const handleConfirmSendNow = async () => {
    if (!canSchedule || isCreating || isUpdating || isDispatching) return;

    try {
      setIsSubmitting(true);
      let targetCampaignId = campaignId;

      if (isEditMode && campaignId) {
        await updateCampaign({
          input: {
            id: campaignId,
            title: title.trim(),
            description: description.trim(),
            groupIds: selectedGroups.map((g) => g.id),
            mediaIds,
            status: CampaignStatus.Draft,
          },
        });
      } else {
        const result = await createCampaign({
          input: {
            workspace: { id: workspaceId },
            title: title.trim(),
            description: description.trim(),
            groupIds: selectedGroups.map((g) => g.id),
            mediaIds,
            status: CampaignStatus.Draft,
            replyCount: 0,
          },
        });
        const created = (
          result as { id?: string } | null | undefined
        );
        targetCampaignId = created?.id;
      }

      if (!targetCampaignId) {
        throw new Error('Send now: failed to resolve campaign id after save');
      }

      await dispatchCampaign({ id: targetCampaignId });

      if (isEditMode && campaignId) {
        onUpdated?.(campaignId);
      } else if (targetCampaignId) {
        onCreated?.(targetCampaignId);
      }
      setColumnOverlay(null);
      // Only clear the form in create mode. In edit mode the panel instance can
      // be reused on reopen; resetting here empties it while the hydration latch
      // (hydratedFromCampaignId) still matches, so the reopened panel would show
      // blank fields until a full reload. The panel closes either way.
      if (!isEditMode) reset();
      onClose?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendNowPress = () => {
    if (!canSchedule) return;
    setColumnOverlay(
      <ConfirmationOverlay
        title={t('campaigns.create.sendNowConfirm.title', 'Send campaign now?')}
        subtitle={t(
          'campaigns.create.sendNowConfirm.body',
          'This will immediately send the campaign to {{count}} asset(s). This cannot be undone.',
          { count: assetCount }
        )}
        confirmLabel={t('campaigns.create.sendNowConfirm.confirm', 'Send now')}
        confirmState="active"
        onConfirm={handleConfirmSendNow}
        cancelLabel={t('common.cancel', 'Cancel')}
        onCancel={() => setColumnOverlay(null)}
        testID="send-now-confirm-overlay"
      />
    );
  };

  // ---- Render ----
  return (
    <View style={styles.container} testID="create-campaign-panel">
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        {/* Groups */}
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>
            {t('campaigns.create.groups', 'Groups')}
          </Text>
          <GroupSelector
            selectedGroups={resolvedSelectedGroups}
            availableGroups={availableGroups}
            onGroupsChange={(groups) =>
              setSelectedGroups(
                groups.map((g) => ({ id: g.id, name: g.name }))
              )
            }
            // '+' opens the groups picker as a sibling slide-in column (like
            // New Asset's groups panel), not an inline dropdown. MultiItemSelector
            // stashes the setter + current selection for the panel to pick up.
            onSelectorEdit={onOpenGroupsPanel}
          />
          {/* Live asset count */}
          <View style={styles.assetCountRow}>
            {isLoadingAssetCount ? (
              <ActivityIndicator
                size="small"
                color={Colors.role.textSecondary}
              />
            ) : (
              <Text
                style={styles.assetCountText}
                testID="asset-count-label"
              >
                {t(
                  'campaigns.create.assetCount',
                  '{{count}} asset(s) in scope',
                  { count: assetCount }
                )}
              </Text>
            )}
          </View>
        </View>

        {/* Title */}
        <CCTextInput
          label={t('campaigns.create.title', 'Title')}
          value={title}
          onChangeText={setTitle}
          placeholder={t(
            'campaigns.create.titlePlaceholder',
            'Campaign title'
          )}
          numberOfLines={1}
          testID="campaign-title-input"
        />

        {/* Description */}
        <CCTextInput
          label={t('campaigns.create.description', 'Description')}
          value={description}
          onChangeText={setDescription}
          placeholder={t(
            'campaigns.create.descriptionPlaceholder',
            'Campaign message to send'
          )}
          numberOfLines={4}
          testID="campaign-description-input"
        />

        {/* Media */}
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>
            {t('campaigns.create.media', 'Attachment')}
          </Text>
          <View style={styles.mediaGrid}>
            {mediaItems.map((item) => {
              const imageUri =
                item.type === MediaType.Image
                  ? item.url ??
                    (item.id && item.extension
                      ? `${EnvConfig.mediaUrl}/images/${item.id}/original.${item.extension}`
                      : undefined)
                  : undefined;
              return (
              <View key={item.id} style={styles.mediaThumb}>
                {imageUri ? (
                  <Image
                    source={{ uri: imageUri }}
                    style={styles.mediaThumbImage}
                    resizeMode="cover"
                  />
                ) : (
                  <View style={styles.mediaThumbFallback}>
                    <Text style={styles.mediaThumbFallbackText} numberOfLines={2}>
                      {item.name ?? item.extension ?? 'file'}
                    </Text>
                  </View>
                )}
                <Pressable
                  onPress={() => handleRemoveMedia(item.id)}
                  style={styles.mediaRemoveButton}
                  accessibilityRole="button"
                  accessibilityLabel={t(
                    'campaigns.create.mediaRemove',
                    'Remove attachment'
                  )}
                  testID={`media-remove-${item.id}`}
                >
                  <Text style={styles.mediaRemoveText}>×</Text>
                </Pressable>
              </View>
              );
            })}
            <MediaPicker
              workspaceId={workspaceId}
              acceptedMediaTypes={[MediaType.Image, MediaType.Video]}
              mode={PickerMode.PICKER}
              setMedia={handleSetMedia}
              setProgress={handleSetProgress}
              setUploading={setIsUploading}
              customTrigger={
                <View
                  style={styles.mediaAddButton}
                  testID="media-add-button"
                >
                  <Text style={styles.mediaAddPlus}>+</Text>
                </View>
              }
            />
          </View>
          {isUploading && (
            <Text style={styles.uploadProgress}>
              {t('campaigns.create.uploading', '{{progress}}%', {
                progress: Math.round(uploadProgress * 100),
              })}
            </Text>
          )}
        </View>

        {/* Schedule date + time */}
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>
            {t('campaigns.create.schedule', 'Schedule')}
          </Text>
          <ScheduleDateTimePicker
            date={scheduledDate}
            time={scheduledTime}
            onDateChange={setScheduledDate}
            onTimeChange={setScheduledTime}
          />
        </View>
      </ScrollView>

      {/* Action buttons — stacked, square (rectangular) */}
      <View style={styles.actions}>
        <Button
          state="active"
          variant="rectangular"
          size="large"
          style={styles.actionButton}
          onPress={handleSchedulePress}
          disabled={!canSchedule || isSubmitting}
          accessibilityLabel={t('campaigns.create.schedule', 'Schedule')}
          testID="schedule-button"
        >
          {t('campaigns.create.scheduleButton', 'Schedule')}
        </Button>
        <Button
          state="default"
          variant="rectangular"
          size="large"
          style={styles.actionButton}
          onPress={handleSendNowPress}
          disabled={!canSchedule || isSubmitting}
          accessibilityLabel={t('campaigns.create.sendNow', 'Send now')}
          testID="send-now-button"
        >
          {t('campaigns.create.sendNowButton', 'Send now')}
        </Button>
        <Button
          state="default"
          variant="rectangular"
          size="large"
          style={styles.actionButton}
          onPress={handleSaveDraft}
          disabled={!canSaveDraft || isSubmitting}
          loading={isSubmitting}
          accessibilityLabel={t('campaigns.create.saveDraft', 'Save as draft')}
          testID="save-draft-button"
        >
          {t('campaigns.create.saveDraft', 'Save as draft')}
        </Button>
      </View>
    </View>
  );
};

// ---------------------------------------------------------------------------
// CreateCampaignPanel — public export; guards workspaceId before mounting inner
// ---------------------------------------------------------------------------

interface CreateCampaignPanelProps {
  /** When set, the panel renders in edit mode and hydrates from the campaign. */
  campaignId?: string;
  onClose?: () => void;
  onCreated?: (campaignId: string) => void;
  onUpdated?: (campaignId: string) => void;
  /** Opens the groups picker in a sibling slide-in column (campaignsPage). */
  onOpenGroupsPanel?: () => void;
}

export const CreateCampaignPanel = ({
  campaignId,
  onClose,
  onCreated,
  onUpdated,
  onOpenGroupsPanel,
}: CreateCampaignPanelProps) => {
  const workspace = useCurrentWorkspace();

  // Guard: do not mount inner component until workspaceId is defined.
  // This prevents passing undefined to mutations (per CLAUDE.md).
  if (!workspace?.id) {
    return null;
  }

  return (
    <CreateCampaignPanelInner
      key={campaignId ?? 'new'}
      workspaceId={workspace.id}
      campaignId={campaignId}
      onClose={onClose}
      onCreated={onCreated}
      onUpdated={onUpdated}
      onOpenGroupsPanel={onOpenGroupsPanel}
    />
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.role.canvas,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: tokens.spacing.lg,
    gap: tokens.spacing.lg,
  },
  section: {
    gap: tokens.spacing.sm,
  },
  // Matches the CCTextInput label styling used by the Title/Description fields
  // in this same form (body.small / medium weight / primary text).
  sectionLabel: {
    ...tokens.typography.body.small,
    fontWeight: '500',
    color: Colors.role.textPrimary,
  },
  assetCountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 20,
  },
  assetCountText: {
    ...tokens.typography.label.medium,
    color: Colors.role.textSecondary,
  },
  mediaGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  mediaThumb: {
    width: 72,
    height: 72,
    borderRadius: tokens.borderRadius.lg,
    overflow: 'hidden',
    backgroundColor: Colors.role.surfaceHover,
    position: 'relative',
  },
  mediaThumbImage: {
    width: '100%',
    height: '100%',
  },
  mediaThumbFallback: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacing.xs,
  },
  mediaThumbFallbackText: {
    ...tokens.typography.label.small,
    color: Colors.role.textSecondary,
    textAlign: 'center',
  },
  // Overlay chrome on an image thumbnail: constant in both modes, because the
  // thing behind it is the user's photo, not a themed surface.
  mediaRemoveButton: {
    position: 'absolute',
    top: tokens.spacing.xxs,
    right: tokens.spacing.xxs,
    width: 20,
    height: 20,
    borderRadius: tokens.borderRadius.full,
    backgroundColor: 'rgba(0,0,0,0.65)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mediaRemoveText: {
    color: tokens.colors.brand.white,
    fontSize: 14,
    lineHeight: 14,
    fontWeight: '700',
  },
  mediaAddButton: {
    width: 72,
    height: 72,
    borderRadius: tokens.borderRadius.lg,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: Colors.role.borderStrong,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.role.canvas,
  },
  mediaAddPlus: {
    fontSize: 28,
    color: Colors.role.textPrimary,
    lineHeight: 28,
  },
  uploadProgress: {
    ...tokens.typography.label.medium,
    color: Colors.role.textSecondary,
  },
  dateTimeRow: {
    flexDirection: 'row',
    gap: tokens.spacing.sm,
  },
  dateInputWrapper: {
    flex: 2,
  },
  timeInputWrapper: {
    flex: 1,
  },
  // Matches DatePickerSelector's `cropCircleStyles.container` (the Crop Circle
  // redesign field shell — neutral[200] bg, no border, 48px, 16px insets).
  timeFieldShell: {
    height: 48,
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: tokens.spacing.lg,
    paddingRight: tokens.spacing.lg,
    borderRadius: tokens.borderRadius.sm,
    backgroundColor: Colors.role.surfaceHover,
  },
  timePickerInnerWrapper: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  actions: {
    flexDirection: 'column',
    gap: tokens.spacing.sm,
    padding: tokens.spacing.lg,
    borderTopWidth: 1,
    borderTopColor: Colors.role.borderSubtle,
    backgroundColor: Colors.role.canvas,
  },
  actionButton: {
    width: '100%',
  },
});
